import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import type { SaveLegalSubpoenaExhibitsPayload } from "@upclaim/sdk/src/data-contracts";

import { track } from "@/lib/analytics";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { HearingsByJuridiction } from "../components/court/hearing-helpers";
import type { ClaimWithRelations } from "./use-claim";

/** Abandon types referential — mirrors SelectAbandonType.fetchAbandonTypes (Vue). */
export function useAbandonTypes() {
  return useQuery({
    queryKey: [...queryKeys.claims.all, "abandon-types"],
    queryFn: () => adminAPI.listAbandonTypes(),
  });
}

/** Batch abandon — same abandon type + shared note for every selected claim. */
export function useBatchAbandonClaims() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      claimIds,
      abandonTypeId,
      internalNote,
    }: {
      claimIds: string[];
      abandonTypeId: number;
      internalNote?: string;
    }) =>
      notify.batchPromise(
        claimIds,
        (claimId) =>
          adminAPI.setClaimAbandonType(claimId, {
            abandonTypeId,
            event: { machineEvent: "Abandon", internalNote: internalNote || undefined },
          }),
        {
          loading: "Abandon des dossiers",
          success: `${claimIds.length} dossier${claimIds.length > 1 ? "s" : ""} abandonné${claimIds.length > 1 ? "s" : ""}`,
          error: "Abandon incomplet",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.all });
    },
  });
}

/** Mirrors SelectAbandonType.confirmChanges (Vue) — sends the "Abandon" machine event. */
export function useSetClaimAbandonType(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ abandonTypeId, internalNote }: { abandonTypeId: number; internalNote?: string }) =>
      notify.promise(
        adminAPI.setClaimAbandonType(claimId, {
          abandonTypeId,
          event: { machineEvent: "Abandon", internalNote: internalNote || undefined },
        }),
        { loading: "Abandon du dossier…", success: "Type d'abandon enregistré" },
      ),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors TopMenu « Re-générer le 1er mail » (Vue). */
export function useRegenerateFirstEmail(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.regenerateFirstEmail(claimId), {
        loading: "Régénération du 1er mail en cours...",
        success: "1er mail régénéré avec succès",
        error: "Erreur lors de la régénération du 1er mail",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      // Same as TopMenu.vue: open the regenerated Front conversation in a new tab.
      const claim = queryClient.getQueryData<ClaimWithRelations>(queryKeys.claims.detail(claimId));
      const newConversationId = claim?.conversation?.frontConversationId;
      if (newConversationId) {
        window.open(`https://app.frontapp.com/open/${newConversationId}`, "_blank")?.focus();
      }
    },
  });
}

/** Mirrors TopMenu / OnePagerCTA one-pager generation (Vue). */
export function useCreateOnePager(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ isMediation }: { isMediation?: boolean }) =>
      notify.promise(adminAPI.createOnePager(claimId, isMediation ? { isMediation: true } : undefined), {
        loading: isMediation
          ? "Génération du One-Pager de mediation en cours..."
          : "Génération du One-Pager en cours...",
        success: isMediation ? "One-Pager de mediation généré avec succès" : "One-Pager généré avec succès",
        error: isMediation
          ? "Erreur lors de la génération du One-Pager de mediation"
          : "Erreur lors de la génération du One-Pager",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

export type RequestableDocumentType = Parameters<typeof adminAPI.requestClaimDocument>[1];

/**
 * Asks the AI for a claim document (eligibility / operating carrier proof). It lands on the claim
 * later, when the AI calls back — nothing to refetch now. A request already in progress is a 409,
 * whose message is shown under the error toast.
 */
export function useRequestClaimDocument(claimId: string) {
  return useMutation({
    mutationFn: (documentType: RequestableDocumentType) =>
      notify.promise(adminAPI.requestClaimDocument(claimId, documentType), {
        loading: "Envoi de la demande...",
        success: "Demande envoyée, le document arrivera sur le dossier",
        error: "Erreur lors de la demande de document",
      }),
  });
}

/** (Re)generates the ESCP Form A as the claim's FORM_A document. */
export function useGenerateFormA(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.generateFormA(claimId), {
        loading: "Génération du formulaire A en cours...",
        success: "Formulaire A généré avec succès",
        error: "Erreur lors de la génération du formulaire A",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors TopMenu « Réinitialiser la timeline » (Vue). */
export function useResetClaimTimeline(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.resetClaimTimeline(claimId), {
        loading: "Réinitialisation de la timeline…",
        success: "Timeline réinitialisée avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors TopMenu « Synchroniser les tickets » (Vue). */
export function useSyncReservationTickets(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reservationId: string) =>
      notify.promise(adminAPI.syncReservationTickets(reservationId), {
        loading: "Synchronisation en cours...",
        success: "Billets synchronisés avec succès",
        error: "Impossible de synchroniser l'historique des tickets",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Booking-certificate preview — mirrors TopMenu « Générer le Certificat de réservation » preview call (Vue). */
export function useBookingCertificatePreview(claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "booking-certificate-preview"],
    queryFn: () => adminAPI.getBookingCertificatePreview(claimId),
    enabled,
  });
}

export type BookingCertificatePreview = Awaited<ReturnType<typeof adminAPI.getBookingCertificatePreview>>;

/** Mirrors TopMenu « Générer le Certificat de réservation » (Vue) — POSTs the preview payload as-is. */
export function useGenerateBookingCertificates(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preview: BookingCertificatePreview) =>
      notify.promise(
        adminAPI.postBookingCertificates(
          claimId,
          preview as Parameters<typeof adminAPI.postBookingCertificates>[1],
        ),
        {
          loading: "Document en cours de génération (~30 sec)...",
          success: "Certificat(s) généré(s) avec succès",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Booking certificates the claim's Insight IA study would generate (original booking, not the rerouting). */
export function useAiStudyBookingCertificatePreview(claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "ai-study-booking-certificate-preview"],
    queryFn: () => adminAPI.getAiStudyBookingCertificatePreview(claimId),
    enabled,
  });
}

export type AiStudyBookingCertificatePreview = Awaited<
  ReturnType<typeof adminAPI.getAiStudyBookingCertificatePreview>
>;

/** (Re)generates the booking certificates from the Insight IA study, server-side. */
export function useGenerateAiStudyBookingCertificates(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.postAiStudyBookingCertificates(claimId), {
        loading: "Document en cours de génération (~30 sec)...",
        success: ({ generatedCount }) =>
          `${generatedCount} certificat${generatedCount > 1 ? "s" : ""} généré${generatedCount > 1 ? "s" : ""} depuis l'étude Insight IA`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Computed itineraries preview (not persisted) — mirrors InitIncident.vue getComputedItineraries. */
export function useComputedItineraries(claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "computed-itineraries"],
    queryFn: () => adminAPI.getComputedItineraries(claimId),
    enabled,
  });
}

export type InitIncidentPayload = Parameters<typeof adminAPI.postIncident>[1];

/** Mirrors InitIncident.vue initIncident — manual (GDS) incident initialization. */
export function useInitIncident(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: InitIncidentPayload) =>
      notify.promise(adminAPI.postIncident(claimId, payload), {
        loading: "Initialisation de l'incident…",
        success: "Incident initialisé avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors StartClaimCTA.generateNotificationDocument (Vue) — raw blob POST. */
export function useGenerateNotificationDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(
        adminAPI.instance.post(`/admin/claims/${claimId}/documents/generate-notification-document`, null, {
          responseType: "blob",
        }),
        {
          loading: "Document en cours de génération (~30 sec à 1min)...",
          success: "La Notification de cession a été ajoutée au dossier",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors StartClaimCTA « Générer le Brouillon » (Vue) — initClaim with force-creation fallback. */
export function useInitClaim(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(
        adminAPI.initClaim(claimId).catch(() =>
          // Quick dirty fix for mail CONNECTION_DISRUPTION when the incident cannot be initialized
          adminAPI.initClaim(claimId, { tmpForceCreation: true }),
        ),
        { loading: "Initialisation en cours", success: "Initialisation réussie" },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors NumeroRG-CTA.setProcedureIdentifier (Vue). */
export function usePutProcedure(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { numeroRG?: string; juridictionId?: number }) =>
      notify.promise(adminAPI.putProcedure(claimId, payload), {
        loading: "Enregistrement…",
        success: "Numéro RG enregistré avec succès",
      }),
    onSuccess: async ({ success }, payload) => {
      if (success) {
        if (payload.numeroRG) {
          track("RG Number Set", { claimId, juridictionId: payload.juridictionId });
        }
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Competent courts for the procedure — mirrors SelectCompetentCourt (Vue). */
export function useCompetentCourts(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "competent-courts"],
    queryFn: () => adminAPI.listCompetentCourtsForClaim(claimId),
  });
}

/** Family claim acquisitions — mirrors InitProcedureCTA.claimAcquisitions (Vue). */
export function useClaimAcquisitions(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "acquisitions"],
    queryFn: () => adminAPI.listClaimAcquisitions(claimId),
  });
}

/** Mirrors InitProcedureCTA.initClaimProcedure (Vue). */
export function useInitClaimProcedure(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      juridictionId: number;
      procedureType: "PROCEDURE_CLASSIQUE" | "ESCP";
      familyClaimAcquisitionsIds: string[];
    }) =>
      notify.promise(adminAPI.initClaimProcedure(claimId, payload), {
        loading: "Initialisation de la procédure…",
        success: "Procédure initialisée avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors UploadAssignationToReview.addClaimDocument (Vue). */
export function useAddClaimDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      name: string | null;
      document?: string;
      mimeType?: string;
      claimDocumentType: string;
    }) =>
      notify.promise(adminAPI.addClaimDocument(claimId, payload), {
        loading: "Assignation en cours d'envoi",
        success: "Assignation transmise avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors TravelAgencySignatureCTA.generateAgencyReceivableDocument (Vue). */
export function useGenerateTravelAgencyReceivable(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.generateTravelAgencyToUpclaimReceivable(claimId), {
        loading: "Cession agence en cours de génération",
        success: "Document cession Agence généré (.docx)",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors TravelAgencySignatureCTA.sendAgencyReceivableDocument (Vue). */
export function useSendTravelAgencyContract(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ isAuto }: { isAuto: boolean }) =>
      notify.promise(adminAPI.sendTravelAgencyContract(claimId, { isAuto }), {
        loading: "Signature agence en cours de préparation (~30 sec à 1min)...",
        success: isAuto ? "Document généré, prêt à être signé" : "Demande de signature envoyée à l'agence",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors TravelAgencySignatureCTA.deleteTravelAgencySignature (Vue). */
export function useDeleteTravelAgencyContract(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.deleteTravelAgencyContract(claimId), {
        loading: "Suppression de la signature…",
        success: "Signature supprimée avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Formal notice of the claim — mirrors FormalNoticeCTA.fetch (Vue). */
export function useClaimFormalNotice(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "formal-notice"],
    queryFn: () => adminAPI.retrieveClaimFormalNotice(claimId),
  });
}

/** Mirrors FormalNoticeCTA / GenerateFormalNotice.deleteFormalNotice (Vue). */
export function useDeleteFormalNotice(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.deleteFormalNotice(claimId), {
        loading: "Suppression…",
        success: "Documents de preuve supprimés",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors FormalNoticeCTA.sendFormalNotice (Vue). */
export function useSendFormalNotice(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.sendFormalNotice(claimId, {}), {
        loading: "Envoi de la Mise en Demeure via AR24…",
        success: "Mise en Demeure envoyée",
        error: "Erreur lors de l'envoi de la Mise en Demeure",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GenerateFormalNotice.generateFormalNoticeProject (Vue). */
export function useGenerateFormalNoticeProject(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (claimDocumentsIds: string[]) =>
      notify.promise(adminAPI.generateFormalNoticeProject(claimId, { claimDocumentsIds }), {
        loading: "La Mise en demeure est en train d'être générée (1 à 2 minutes)…",
        success: "Projet de MED généré avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GenerateFormalNotice.mergeFormalNotice (Vue). */
export function useMergeFormalNotice(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.mergeFormalNotice(claimId), {
        loading: "La Mise en demeure est en train d'être fusionnée (1 à 3 minutes)…",
        success: "Mise en Demeure finale générée avec succès",
        error: "Erreur lors de la fusion de la Mise en demeure",
      }),
    onSuccess: async () => {
      track("Formal Notice Generated", { claimId });
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GenerateFormalNotice.updateFormalNoticeDocuments (Vue). */
export function usePutFormalNotice(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (claimDocumentIds: string[]) =>
      notify.promise(adminAPI.putFormalNotice(claimId, { claimDocumentIds }), {
        loading: "Mise à jour des documents",
        success: () => ({
          message: "Mise en Demeure mise à jour",
          description: "Note: Penser à modifier la liste des documents (dans le .docx)",
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Legal subpoena of the claim — mirrors LegalSubpeonaCTA.fetch (Vue). */
export function useLegalSubpoena(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "legal-subpoena"],
    queryFn: () => adminAPI.retrieveLegalSubpeona(claimId),
  });
}

/** Default ordered documents for a new subpoena — mirrors GenerateLegalSubpeona (Vue). */
export function useOrderedClaimDocuments(claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "ordered-documents"],
    queryFn: () => adminAPI.getOrderedClaimDocuments(claimId),
    enabled,
  });
}

/** Mirrors GenerateLegalSubpeona.generateLegalSubpeonaProject (Vue). */
export function useGenerateLegalSubpoenaProject(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // The whole reviewed arrangement, not just the ids: from ids alone the API can only
    // guess the numbers, and put every added document at the end of the bordereau.
    mutationFn: (exhibits: SaveLegalSubpoenaExhibitsPayload["exhibits"]) =>
      notify.promise(
        adminAPI.generateLegalSubpoenaProject(claimId, {
          claimDocumentsIds: exhibits.map(({ claimDocumentId }) => claimDocumentId),
          exhibits,
        }),
        {
          loading: "Le projet d'assignation est en train d'être généré (1 à 2 minutes)…",
          success: "Projet d'assignation généré avec succès",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/**
 * The stored "Pièce n°X" numbering of a claim's subpoena (TECH-1047) — the stored
 * arrangement once a project exists, the computed default before that.
 */
export function useLegalSubpoenaExhibits(claimId: string, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "legal-subpoena-exhibits"],
    queryFn: () => adminAPI.getLegalSubpoenaExhibits(claimId),
    enabled,
  });
}

/** Persist a jurist's arrangement of the exhibits before generating the project. */
export function useSaveLegalSubpoenaExhibits(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // `notify.promise` always toasts — omitting the messages only falls back to
    // "Envoi de la requête…" / "Opération réussie", which says nothing about what
    // was saved. The review view debounces, so this is one toast per burst of edits,
    // not one per keystroke.
    mutationFn: (exhibits: SaveLegalSubpoenaExhibitsPayload["exhibits"]) =>
      notify.promise(adminAPI.saveLegalSubpoenaExhibits(claimId, { exhibits }), {
        loading: "Sauvegarde des modifications…",
        success: "Modifications sauvegardées",
        error: "La numérotation des pièces n'a pas pu être sauvegardée",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GenerateLegalSubpeona.mergeLegalSubpeona (Vue). */
export function useMergeLegalSubpoena(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.mergeLegalSubpoena(claimId), {
        loading: "L'Assignation est en train d'être fusionnée (1 à 3 minutes)…",
        success: "Assignation finale générée avec succès",
        error: "Erreur lors de la fusion de l'Assignation",
      }),
    onSuccess: async () => {
      track("Legal Subpoena Generated", { claimId });
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors MergeAssignationCTA.generateAssignation (Vue). */
export function useGenerateAssignationWithStampedPieces(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.generateAssignationWithStampedPieces>[1]) =>
      notify.promise(adminAPI.generateAssignationWithStampedPieces(claimId, payload), {
        loading: "Fusion de l'assignation et des pièces en cours (quelques minutes)…",
        success: "Assignation générée avec succès",
        error: "Erreur lors de la fusion",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Contact emails used for the validation feedback — mirrors ClaimValidationCTA (Vue). */
export function useClaimContactEmails(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "contact-emails"],
    queryFn: () => adminAPI.retrieveClaimContactEmails(claimId),
  });
}

/** Confidence score — mirrors ClaimValidationCTA (Vue). */
export function useClaimConfidenceScore(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "confidence-score"],
    queryFn: () => adminAPI.retrieveClaimConfidenceScore(claimId),
  });
}

/** Mirrors ClaimValidationCTA.confirmClaim (Vue). */
export function useAskForAgentFeedback(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentEmail: string) =>
      notify.promise(adminAPI.validation_askForAgentFeedback(claimId, { agentEmail }), {
        loading: "Validation et envoi de l'email en cours",
        success: `Demande de feedback envoyée à ${agentEmail}`,
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors GeneratePleadingBriefCTA.generatePleadingBrief (Vue). */
export function useGeneratePleadingBrief(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.generatePleadingBrief>[1]) =>
      notify.promise(adminAPI.generatePleadingBrief(claimId, payload), {
        loading: "Génération du dossier de plaidoirie en cours (2 à 5 minutes)…",
        success: "Dossier de plaidoirie généré avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GeneratePleadingBriefCTA UPLOAD source — adds the conclusions as a procedure document. */
export function useAddProcedureDocument(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.addProcedureDocument>[1]) =>
      notify.promise(adminAPI.addProcedureDocument(claimId, payload), {
        loading: "Ajout du document de procédure…",
        success: "Document de procédure ajouté",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GenerateStampedExhibitsCTA.generateStampedExhibits (Vue). */
export function useGenerateStampedExhibits(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.generateStampedExhibits>[1]) =>
      notify.promise(adminAPI.generateStampedExhibits(claimId, payload), {
        loading: "Génération des pièces tamponnées en cours (quelques minutes)…",
        success: "Pièces tamponnées générées avec succès",
        error: "Erreur lors de la génération des pièces tamponnées",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors GenerateStampedExhibitsCTA.markAsTransmitted (Vue). */
export function useMarkExhibitsTransmitted(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.markExhibitsTransmittedToAdversary(claimId), {
        loading: "Transmission en cours…",
        success: "Pièces marquées comme transmises",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Front conversation messages preview — mirrors ConversationSelectionModal.fetchMessages (Vue). */
export function useClaimConversations(claimId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "front-conversations"],
    queryFn: () => adminAPI.listClaimConversations(claimId),
    enabled,
  });
}

/** Mirrors ConversationSelectionModal.extract (Vue). */
export function useExtractFrontConversation(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageIds: string[]) =>
      notify.promise(adminAPI.extractFrontConversation(claimId, { messageIds }, { force: true }), {
        loading: "Extraction de la conversation en cours...",
        success: "Conversation extraite avec succès",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Receivable contracts with signature status — mirrors ReceivablesContractsCTA (Vue). */
export function useReceivableContracts(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "receivable-contracts"],
    queryFn: () => adminAPI.listReceivableContracts(claimId),
  });
}

/** PNR contacts — mirrors ReceivablesContractsCTA (Vue); a failure yields an empty list. */
export function usePnrContacts(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "pnr-contacts"],
    queryFn: () => adminAPI.retrievePnrContacts(claimId).catch(() => []),
  });
}

/** Mirrors ReceivablesContractsCTA.generateReceivables (Vue) — raw blob POST. */
export function useGenerateReceivables(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(
        adminAPI.instance.post(`/admin/claims/${claimId}/documents/receivable-contract`, null, {
          responseType: "blob",
        }),
        {
          loading: "Document en cours de génération (~30 sec à 1min)...",
          success: "Documents générés avec succès",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors ReceivablesContractsCTA « Transmettre les cessions à l'agence » (Vue). */
export function useNotifyReceivablesReady(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) =>
      notify.promise(adminAPI.validation_notifyReceivablesContractsReady(claimId, { email }), {
        loading: `Envoie à ${email}...`,
        success: `Notification envoyée par mail à ${email}`,
        analytics: { action: "Transmettre les cessions à l'agence", props: { claimId } },
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors ReceivablesContractsCTA.sendSignature (Vue). */
export function useLaunchFamilyAcquisitionWorkflow(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      familyId,
      ...payload
    }: {
      familyId: string;
      boxFileId: string;
    } & (
      | { signerFullName: string; signerEmail: string }
      // A family whose passengers are all minors signs through a parent or guardian, who
      // replaces the signer name/email entirely.
      | { legalRepresentative: { firstName: string; lastName: string; email: string } }
    )) =>
      notify.promise(adminAPI.launchFamilyAcquisitionWorkflow(claimId, familyId, payload), {
        loading: "Préparation de la signature en cours",
        success: "Signature créée avec succès",
        error: "Une erreur est survenue lors de la création de la signature",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors Court.vue syncProcedure — sync Tribunal Digital (date d'audience + documents). */
export function useSyncClaimProcedure(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.syncClaimProcedure(claimId), {
        loading: "Synchronisation en cours…",
        success: "Synchronisation effectuée",
        error: "Erreur lors de la synchronisation",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Ongoing hearings grouped by juridiction — mirrors HearingSelection.fetchHearings (Vue). */
export function useProcedureHearingsByJuridiction(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "procedure-hearings"],
    queryFn: async () =>
      (await adminAPI.listProcedureHearings({
        procedure: "ongoing",
        groupBy: "juridiction",
      })) as unknown as HearingsByJuridiction,
  });
}

/** Mirrors HearingSelection.setHearing (Vue) — upsert de la prochaine audience. */
export function useUpsertNextHearing(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      juridictionId: number;
      courtHearingId: number;
      hearingTypeId: number;
      lawyerId: number;
      procedureType: "PROCEDURE_CLASSIQUE" | "ESCP";
    }) =>
      notify.promise(adminAPI.upsertNextHearing(claimId, payload), {
        loading: "Enregistrement de l'audience…",
        success: "L'audience à été crée",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        track("Hearing Scheduled", { claimId });
        // Also refetches the "procedure-hearings" key (claim-detail prefix).
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Port of services/api/download-file-from-response.ts (Vue) — saves a blob response as a file. */
function downloadFileFromResponse(response: AxiosResponse<Blob>) {
  const contentDisposition = response.headers["content-disposition"] as string | undefined;
  const filename =
    contentDisposition
      ?.split("; ")
      .find((param) => param.startsWith("filename="))
      ?.split("=")[1] ?? "Document.pdf";

  const blob = new Blob([response.data], {
    type: (response.headers["content-type"] as string | undefined) ?? "application/pdf",
  });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = decodeURIComponent(filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

/** Mirrors GenerateMediationFormArchive.generateArchive (Vue) — raw blob POST, then browser download. */
export function useGenerateMediationFormArchive(claimId: string) {
  return useMutation({
    mutationFn: (claimDocumentIds: string[]) =>
      notify.promise(
        adminAPI.instance
          .post<Blob>(
            `/admin/claims/${claimId}/mediation-form-archive`,
            { claimDocumentIds },
            { responseType: "blob" },
          )
          .then(downloadFileFromResponse),
        {
          loading: "Génération de l'archive de médiation... (1 à 3 minutes)",
          success: "Archive de médiation générée avec succès",
          error: "Erreur lors de la génération de l'archive",
        },
      ),
  });
}

/** Envoie à une famille le lien de sa page de suivi (email « lien de suivi partagé »). */
export function useSendTrackingLinkEmail(claimId: string, familyClaimAcquisitionId: string, toEmail: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.sendTrackingLinkEmail(familyClaimAcquisitionId), {
        loading: "Envoi du lien de suivi…",
        success: `Lien de suivi envoyé à ${toEmail}`,
      }),
    // Rafraîchit « Lien envoyé le … » (les acquisitions vivent sous la clé du dossier).
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/**
 * Corrige l'adresse email d'une acquisition — typiquement après un bounce.
 *
 * L'API écrit `acquisition.toEmail` **et** `family.email` dans la même
 * transaction : les séquences lisent l'un, les emails de signature et de
 * quittance lisent l'autre. Le lien de signature déjà envoyé reste valide (le
 * JWT ne porte que des identifiants).
 */
export function useUpdateAcquisitionEmail(claimId: string, familyClaimAcquisitionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      notify.promise(adminAPI.updateAcquisitionEmail(claimId, familyClaimAcquisitionId, { email }), {
        loading: "Mise à jour de l'email…",
        success: "Email mis à jour",
        analytics: { action: "Update acquisition email" },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/**
 * Marque l'ouverture comme une prévisualisation admin. La page de suivi lit ce
 * paramètre pour s'exclure des statistiques passager : sans lui, chaque coup
 * d'œil d'un admin gonflerait la moyenne d'ouvertures par dossier.
 */
function withPreviewMarker(url: string): string {
  const previewUrl = new URL(url);
  previewUrl.searchParams.set("preview", "admin");
  return previewUrl.toString();
}

/**
 * Ouvre la page de suivi telle que la voit le passager. Rien n'est envoyé : le
 * lien porte un token court qui remplace le code à 6 chiffres.
 *
 * L'onglet est ouvert **avant** l'await — un `window.open` après une promesse
 * est bloqué comme popup par Safari et Chrome.
 */
export function useOpenTrackingPreview(familyClaimAcquisitionId: string) {
  return useMutation({
    mutationFn: async () => {
      // Le placeholder doit être ouvert AVANT l'await, sinon le navigateur
      // bloque le popup. Surtout : pas de `noopener` dans les features — la
      // spec impose alors un retour `null`, l'onglet reste sur about:blank et
      // n'est jamais navigué. On coupe le lien avec l'ouvreur à la main.
      const tab = window.open("", "_blank");
      if (tab) {
        tab.opener = null;
      }
      try {
        const { url } = await notify.promise(adminAPI.getClaimTrackingPreviewLink(familyClaimAcquisitionId), {
          loading: "Ouverture de la page de suivi…",
          success: "Page de suivi ouverte",
        });
        const previewUrl = withPreviewMarker(url);
        if (tab) {
          // replace(), so the blank placeholder doesn't become a history entry
          // the passenger page can go "back" to.
          tab.location.replace(previewUrl);
        } else {
          // Popup blocked outright — worth one direct attempt.
          window.open(previewUrl, "_blank", "noopener");
        }
        return previewUrl;
      } catch (error) {
        tab?.close();
        throw error;
      }
    },
  });
}

/**
 * Downloads a Box/S3 file through its signed URL — mirrors MergeAssignationCTA.downloadFromBoxFileId (Vue).
 * Fetches the bytes and forces a download from an object URL, so the file lands in
 * downloads rather than opening inline. Cross-origin, so the claim-documents bucket
 * CORS must allow the app origin (see scripts/apply-cors-add-react-origin.ts).
 */
export async function downloadFromBoxFileId(fileId: string, filename: string) {
  const signedUrl = await adminAPI.retrieveFile(fileId);
  if (!signedUrl) {
    throw new Error("URL de téléchargement indisponible");
  }

  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new Error(`Échec du téléchargement (${response.status})`);
  }
  const blob = await response.blob();

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}
